import { useMemo, useState } from "react";
import { useLocation, useOutletContext } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";

import { ShellSubrail } from "../components/shell/ShellSubrail";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { fetchRouteRollout, type RouteRolloutRow } from "../data/rollout";
import { useReadModel } from "../data/useReadModel";
import type { LiveConfig } from "../lib/live-config";
import type { ShellOutletContext } from "../nav/AppShell";
import { CheckpointRollout } from "../surfaces/CheckpointRollout";
import { MaterialVerification } from "../surfaces/MaterialVerification";
import { RolloutStatus } from "../surfaces/RolloutStatus";
import { TokenMinter } from "../surfaces/TokenMinter";
import { TokenMintSessionProvider, useTokenMintSession } from "../surfaces/token-mint-session";

// Patrols workbench (console shell v3, Wave 1) - rollout readiness regrouped behind a
// subrail instead of the endless single-column resume list. The head band owns the first
// viewport: how many routes are ready, how many required checkpoints are proven, and how
// many blockers need a decision. Ready checkpoints already collapse to the proof rollup
// inside the Checkpoints section; only attention stays loud. The minter section exists only
// for tokens.mint holders; the surface itself also fails closed so the hook never reads
// unless the key is present.

export const PATROLS_WB_SECTIONS = [
  { key: "routes", id: "patrol-routes", label: "Routes" },
  { key: "checkpoints", id: "patrol-checkpoints", label: "Checkpoints" },
  { key: "material", id: "patrol-material", label: "Material" },
  { key: "minting", id: "patrol-minting", label: "Minting" },
] as const;

export type PatrolsWbSectionKey = (typeof PATROLS_WB_SECTIONS)[number]["key"];

/** Legacy in-page anchors (guide-dock era) resolve to the section that now owns them. */
const LEGACY_ANCHOR_SECTIONS: Readonly<Record<string, PatrolsWbSectionKey>> = {
  "patrol-routes": "routes",
  "patrol-checkpoints": "checkpoints",
  "patrol-material": "material",
  "patrol-minting": "minting",
};

interface RolloutSummary {
  routeCount: number;
  routesReady: number;
  requiredTotal: number;
  readyTotal: number;
  blockerTotal: number;
}

export function summarizeRollout(rows: readonly RouteRolloutRow[]): RolloutSummary {
  const summary: RolloutSummary = {
    routeCount: rows.length,
    routesReady: 0,
    requiredTotal: 0,
    readyTotal: 0,
    blockerTotal: 0,
  };
  for (const row of rows) {
    if (row.rolloutStatus === "ready") summary.routesReady += 1;
    summary.requiredTotal += row.requiredCount;
    summary.readyTotal += row.readyCount;
    // Open blocker CONDITIONS, not blocked checkpoints: the summary view only ever emits
    // missing-material and missing-geofence blockers (the QR/NFC terms are dead since the
    // 20260612200500 rollout view), and a checkpoint missing both genuinely has two
    // things to fix. The dead terms are excluded so the number cannot silently inflate.
    summary.blockerTotal += row.missingMaterialCount + row.missingGeofenceCount;
  }
  return summary;
}

export function PatrolsPage() {
  const { client, config, can } = useOutletContext<ShellOutletContext>();
  const showMinting = can("tokens.mint");

  const workbench = <PatrolsWorkbench client={client} config={config} showMinting={showMinting} />;
  return showMinting ? (
    <TokenMintSessionProvider client={client} accountId={config.accountId} enabled>
      {workbench}
    </TokenMintSessionProvider>
  ) : (
    workbench
  );
}

/**
 * Rendered inside the mint session provider so the head band's rollout read shares the
 * mint generation the Routes section uses - after a mint or replace, the head numbers and
 * the route cards refresh together instead of drifting apart.
 */
function PatrolsWorkbench({
  client,
  config,
  showMinting,
}: {
  client: SupabaseClient;
  config: LiveConfig;
  showMinting: boolean;
}) {
  const location = useLocation();
  const [active, setActive] = useState<PatrolsWbSectionKey>(() => {
    const anchor = location.hash.replace(/^#/, "");
    return LEGACY_ANCHOR_SECTIONS[anchor] ?? "routes";
  });
  const session = useTokenMintSession();
  const mintGeneration = session?.mintGeneration ?? 0;
  // First-viewport progress band (board frame 08): one aggregate read of the same rollout
  // summary the Routes section renders per route, on the same mint-generation key.
  const rollout = useReadModel<RouteRolloutRow[]>(
    () => fetchRouteRollout(client, config.accountId),
    [client, config.accountId, mintGeneration],
  );
  const summary = useMemo(() => summarizeRollout(rollout.data ?? []), [rollout.data]);

  const sections = showMinting
    ? PATROLS_WB_SECTIONS
    : PATROLS_WB_SECTIONS.filter((section) => section.key !== "minting");
  const activeKey = active === "minting" && !showMinting ? "routes" : active;

  return (
    <WorkbenchLayout
      className="wb--panes patrols-page"
      subrail={
        <ShellSubrail
          eyebrow="Patrols"
          items={sections.map(({ key, label }) => ({ key, label }))}
          activeKey={activeKey}
          onSelect={(key) => setActive(key as PatrolsWbSectionKey)}
        />
      }
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <h1 className="wb-head__title">Patrols - rollout readiness</h1>
            <p className="wb-head__note">
              Route and checkpoint setup readiness, plus field proof that the latest checkpoint
              material was actually scanned after creation.
            </p>
          </div>
          {rollout.data !== null ? (
            <dl className="wb-metrics" aria-label="Rollout readiness">
              <div className="wb-metric">
                <dt className="wb-metric__label">Routes ready</dt>
                <dd className="wb-metric__value">
                  {summary.routesReady}/{summary.routeCount}
                </dd>
              </div>
              <div className="wb-metric">
                <dt className="wb-metric__label">Required checkpoints</dt>
                <dd className="wb-metric__value">
                  {summary.readyTotal}/{summary.requiredTotal}
                </dd>
                <dd className="wb-metric__sub">ready of required</dd>
              </div>
              <div className={summary.blockerTotal > 0 ? "wb-metric wb-metric--warn" : "wb-metric"}>
                <dt className="wb-metric__label">Open blockers</dt>
                <dd className="wb-metric__value">{summary.blockerTotal}</dd>
                <dd className="wb-metric__sub">
                  {summary.blockerTotal > 0 ? "material or geofence gaps to clear" : "nothing waiting"}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>
      }
    >
      <section
        id="patrol-routes"
        className="wb-section patrols__section"
        aria-label="Route rollout status"
        hidden={activeKey !== "routes"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Route rollout</h2>
          <p className="page__note">
            Which patrol routes are ready to roll out and what still blocks go-live. Attention
            sorts first.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <RolloutStatus client={client} accountId={config.accountId} canMint={showMinting} />
          </div>
        </div>
      </section>

      <section
        id="patrol-checkpoints"
        className="wb-section patrols__section"
        aria-label="Checkpoint readiness"
        hidden={activeKey !== "checkpoints"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Checkpoint readiness</h2>
          <p className="page__note">
            Per-checkpoint readiness: coordinates, geofence, and verification method. Ready
            checkpoints collapse to one proof line; full cards render only where attention is real.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <CheckpointRollout client={client} accountId={config.accountId} canMint={showMinting} />
          </div>
        </div>
      </section>

      <section
        id="patrol-material"
        className="wb-section patrols__section"
        aria-label="Checkpoint material verification"
        hidden={activeKey !== "material"}
      >
        <div className="wb-section__intro">
          <h2 className="wb-section__title">Material verification</h2>
          <p className="page__note">
            Field proof that QR/NFC tags were created and scanned after install.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <MaterialVerification client={client} accountId={config.accountId} canMint={showMinting} />
          </div>
        </div>
      </section>

      {showMinting ? (
        <section
          id="patrol-minting"
          className="wb-section patrols__section"
          aria-label="Token minting"
          hidden={activeKey !== "minting"}
        >
          <div className="wb-section__intro">
            <h2 className="wb-section__title">Token minting</h2>
            <p className="page__note">
              Route checkpoint kits (QR / NFC). Minting runs behind an explicit confirmation;
              the kit shows exactly once - print or write tags immediately, then discard.
            </p>
          </div>
          <div className="shell-pane wb-section__pane">
            <div className="shell-pane__scroll wb-section__scroll">
              <TokenMinter client={client} accountId={config.accountId} canMint />
            </div>
          </div>
        </section>
      ) : null}
    </WorkbenchLayout>
  );
}
